<script lang="ts">
  import { faDownload } from '@fortawesome/free-solid-svg-icons';
  import { EXPORT_FORMATS } from '$lib/core/export';
  import { viewContents } from '$lib/core/scene';
  import { DRAWING_FILE_EXTENSION, DRAWING_FILE_MIME, writeDrawingFile } from '$lib/core/script';
  import { downloadText, slugify } from '$lib/services/download';
  import type { EditorState } from '../editor-state.svelte';
  import Icon from './Icon.svelte';

  const { editor }: { editor: EditorState } = $props();

  /** The drawing file's choice; every other is an `EXPORT_FORMATS` id. */
  const DRAWING = 'drawing';
  /** The whole drawing's choice; every other is a view's id. */
  const WHOLE = 'whole';

  let open = $state(false);
  let kind = $state(DRAWING);
  let target = $state(WHOLE);

  const format = $derived(EXPORT_FORMATS.find((each) => each.id === kind) ?? null);
  // A view picked before the script changed may be gone; the whole drawing stands in for it.
  const view = $derived(editor.scene.views.find((each) => each.id === target) ?? null);

  function download() {
    open = false;
    const title = view && format ? `${editor.document.name} ${view.name}` : editor.document.name;
    if (!format) {
      const text = writeDrawingFile({
        name: editor.document.name,
        source: editor.source,
        params: editor.params,
        layers: editor.layers,
        settings: editor.settings,
      });
      downloadText(`${slugify(title)}.${DRAWING_FILE_EXTENSION}`, DRAWING_FILE_MIME, text);
      return;
    }
    const { drawn, bounds } = view
      ? viewContents(editor.scene, view)
      : { drawn: editor.scene, bounds: undefined };
    const text = format.write(drawn, editor.document, bounds);
    downloadText(`${slugify(title)}.${format.extension}`, format.mimeType, text);
  }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="menu" onmouseleave={() => (open = false)}>
  <button
    type="button"
    title="Download the drawing as a file to import later, or as SVG or DXF"
    aria-expanded={open}
    aria-controls="download"
    onclick={() => (open = !open)}
  >
    <Icon icon={faDownload} />
    Download
  </button>

  {#if open}
    <section id="download" class="sheet" aria-label="Download">
      <fieldset>
        <legend>Format</legend>
        <label>
          <input type="radio" bind:group={kind} value={DRAWING} />
          Drawing file (.{DRAWING_FILE_EXTENSION})
        </label>
        {#each EXPORT_FORMATS as each (each.id)}
          <label>
            <input type="radio" bind:group={kind} value={each.id} />
            {each.label}
          </label>
        {/each}
      </fieldset>
      {#if format && editor.scene.views.length > 0}
        <fieldset>
          <legend>What</legend>
          <label>
            <input type="radio" bind:group={target} value={WHOLE} />
            Whole drawing
          </label>
          {#each editor.scene.views as each (each.id)}
            <label>
              <input type="radio" bind:group={target} value={each.id} />
              {each.name}
            </label>
          {/each}
        </fieldset>
      {/if}
      <button type="button" class="go" onclick={download}>
        <Icon icon={faDownload} />
        Download
      </button>
    </section>
  {/if}
</div>

<style>
  .menu {
    position: relative;
  }

  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    background: var(--color-bg);
    color: var(--color-text);
    font: inherit;
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .sheet {
    position: absolute;
    z-index: 10;
    top: 100%;
    right: 0;
    display: grid;
    gap: var(--space-2);
    min-width: 13rem;
    padding: var(--space-3);
    border: 1px solid var(--color-border);
    border-radius: var(--radius);
    background: var(--color-surface);
    box-shadow: 0 12px 30px rgb(0 0 0 / 0.18);
    font-size: 0.8125rem;
  }

  fieldset {
    display: grid;
    gap: 4px;
    margin: 0;
    padding: 0;
    border: 0;
  }

  legend {
    margin-bottom: 4px;
    padding: 0;
    color: var(--color-text-muted);
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  label {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    white-space: nowrap;
    cursor: pointer;
  }

  .go {
    justify-content: center;
    border-color: var(--color-accent);
    background: var(--color-accent);
    color: white;
  }
</style>
